import moment from 'moment'
import { MINUTES_IN_DAY, MINUTES_IN_HOUR } from 'utils/constants'
import { getAllVisits } from '../../../../../VisitSchedules/routes/VisitSchedulesPage/utils/visitsScheduleUtils'
import { clampStudyDay } from '../constants'
import { getVisitStudyDayNumber, resolveVisitAnchorStudyDay } from './visitAnchorStudyDay'
import { allCyclesForArms, findArmById, getResolvedCycleStudyDayBounds } from './cycleResolvedStudyDays'

const offsetToDayDelta = offset => {
  if (!offset) return 0
  const value = Number(offset.value ?? offset.amount ?? 0)
  if (!Number.isFinite(value)) return 0
  const unit = String(offset.unit || 'days').toLowerCase()
  const direction = offset.direction === 'before' ? -1 : 1
  let days = value
  if (unit.startsWith('minute')) days = value / MINUTES_IN_DAY
  else if (unit.startsWith('hour')) days = value / MINUTES_IN_HOUR
  return direction * days
}

const applyOffsetToBaseDay = (baseDay, offset) => {
  const base = Number(baseDay)
  if (!Number.isFinite(base)) return null
  const projected = base + offsetToDayDelta(offset)
  const day = Math.max(1, Math.round(projected))
  return clampStudyDay(day) ?? day
}

const absoluteAnchorStudyDay = (absoluteAt, useAbsoluteStartDate, absoluteStartDate) => {
  if (!absoluteAt) return null
  const anchorMoment = moment.utc(absoluteAt, moment.ISO_8601, true)
  if (!anchorMoment.isValid()) return null
  if (!useAbsoluteStartDate || !absoluteStartDate) return 1
  const startMoment = moment.utc(absoluteStartDate).startOf('day')
  if (!startMoment.isValid()) return null
  const day = anchorMoment.startOf('day').diff(startMoment, 'days') + 1
  return day < 1 ? null : day
}

const visitsForArm = (arms, armId) => {
  if (!armId || !arms?.length) return []
  const arm = findArmById(arms, armId)
  if (!arm) return []
  const fromCycles = getAllVisits([arm]) || []
  const fromArmLevel = Array.isArray(arm.visits) ? arm.visits : []
  const merged = new Map()
  ;[...fromCycles, ...fromArmLevel].forEach(visit => {
    if (!visit) return
    const key = visit.id != null ? String(visit.id) : null
    if (key) merged.set(key, visit)
    else merged.set(`${merged.size}`, visit)
  })
  return [...merged.values()]
}

const armCycleStudyDays = (arms, armId) => {
  const arm = findArmById(arms, armId)
  const scopedArms = arm ? [arm] : []
  const allCycles = allCyclesForArms(scopedArms)
  const startDays = []
  const endDays = []
  allCycles.forEach(cycle => {
    const bounds = getResolvedCycleStudyDayBounds(cycle, allCycles)
    if (!bounds) return
    startDays.push(bounds.start)
    endDays.push(bounds.end)
  })
  return { startDays, endDays }
}

/** Arm start/end uses the earliest/latest of visit days and cycle projected days on that arm. */
const armEntityStudyDay = (arms, armId, point) => {
  const visits = visitsForArm(arms, armId)
  const visitDays = visits.map(getVisitStudyDayNumber).filter(d => d != null)
  const { startDays: cycleStartDays, endDays: cycleEndDays } = armCycleStudyDays(arms, armId)

  if (point === 'end') {
    const endCandidates = [...visitDays, ...cycleEndDays]
    if (endCandidates.length) return Math.max(...endCandidates)
    if (cycleStartDays.length) return Math.max(...cycleStartDays)
    return null
  }

  const startCandidates = [...visitDays, ...cycleStartDays]
  if (startCandidates.length) return Math.min(...startCandidates)
  // Arm exists but has no cycles/visits with study days yet (e.g. empty phases[].cycles).
  if (point === 'start' && findArmById(arms, armId)) return 1
  return null
}

const phaseEntityStudyDay = (arms, phaseId, point) => {
  if (!phaseId || !arms?.length) return null
  const allCycles = allCyclesForArms(arms)
  const cycleDays = []
  const cycleEndDays = []
  arms.forEach(arm => {
    ;(arm.phases || []).forEach(phase => {
      if (String(phase.id) !== String(phaseId)) return
      ;(phase.cycles || []).forEach(cycle => {
        const bounds = getResolvedCycleStudyDayBounds(cycle, allCycles)
        if (!bounds) return
        cycleDays.push(bounds.start)
        cycleEndDays.push(bounds.end)
      })
    })
  })
  if (point === 'end' && cycleEndDays.length) return Math.max(...cycleEndDays)
  if (cycleDays.length) return Math.min(...cycleDays)
  return null
}

const cycleEntityStudyDay = (arms, cycleId, point, armId = null) => {
  if (!cycleId || !arms?.length) return null
  const allCycles = allCyclesForArms(arms)
  const startDays = []
  const endDays = []
  arms.forEach(arm => {
    if (armId != null && arm?.id != null && String(arm.id) !== String(armId)) return
    ;(arm.phases || []).forEach(phase => {
      ;(phase.cycles || []).forEach(cycle => {
        if (String(cycle.id) !== String(cycleId)) return
        const bounds = getResolvedCycleStudyDayBounds(cycle, allCycles)
        if (!bounds) return
        startDays.push(bounds.start)
        endDays.push(bounds.end)
      })
    })
  })
  if (point === 'end' && endDays.length) return Math.max(...endDays)
  if (startDays.length) return point === 'end' ? Math.max(...startDays) : Math.min(...startDays)
  return null
}

export const resolveEngineAnchorStudyDay = (anchor, context = {}) => {
  if (!anchor?.type) return null
  const {
    useAbsoluteStartDate = false,
    absoluteStartDate = null,
    visits = [],
    arms = [],
    selectedArmVisitIds = null,
    selectedArmId = null,
    resolveInstrumentStudyDay = null,
    projectionMode = 'template',
    participantVisits = null,
  } = context

  const type = String(anchor.type)
  const point = anchor.point === 'end' ? 'end' : 'start'
  const offset = anchor.offset

  let baseDay = null

  if (
    type === 'participant_start' ||
    type === 'participant_start_date' ||
    type === 'participant_study_start'
  ) {
    baseDay = 1
  } else if (type === 'participant_login' || type === 'participant_first_login') {
    baseDay = 1
  } else if (type === 'absolute' || type === 'exact_date' || type === 'fixed_calendar_date') {
    const at =
      anchor.absolute_at ||
      anchor.date ||
      anchor.fixed_date?.value ||
      anchor.date_value ||
      ''
    baseDay = absoluteAnchorStudyDay(at, useAbsoluteStartDate, absoluteStartDate)
  } else if (type === 'visit') {
    baseDay = resolveVisitAnchorStudyDay(anchor, visits, selectedArmVisitIds, {
      useAbsoluteStartDate,
      absoluteStartDate,
      projectionMode,
      participantVisits,
    })
  } else if (type === 'arm') {
    const armId = anchor.arm_id || anchor.armId
    baseDay = armEntityStudyDay(arms, armId, point)
    if (baseDay == null && armId != null && !findArmById(arms, armId) && selectedArmId) {
      baseDay = armEntityStudyDay(arms, selectedArmId, point)
    }
  } else if (type === 'phase') {
    const phaseId = anchor.phase_id || anchor.phaseId
    baseDay = phaseEntityStudyDay(arms, phaseId, point)
  } else if (type === 'visit_cycle' || type === 'visitCycle') {
    const cycleId = anchor.cycle_id || anchor.cycleId
    const armId = anchor.arm_id || anchor.armId
    baseDay = cycleEntityStudyDay(arms, cycleId, point, armId)
  } else if (type === 'track' || type === 'chapter') {
    baseDay = 1
  } else if (type === 'instrument' && resolveInstrumentStudyDay) {
    const instrumentId = anchor.instrument_id || anchor.instrumentId
    if (instrumentId != null && instrumentId !== '') {
      baseDay = resolveInstrumentStudyDay(String(instrumentId))
    }
  }

  if (baseDay == null) return null
  return applyOffsetToBaseDay(baseDay, offset)
}

export const resolveEngineScheduleStudyDay = (schedule, context = {}) => {
  const anchors = schedule?.deploy?.anchors
  if (!Array.isArray(anchors) || anchors.length === 0) return null

  const days = anchors
    .map(anchor => resolveEngineAnchorStudyDay(anchor, context))
    .filter(day => day != null && Number.isFinite(Number(day)))
    .map(Number)

  if (!days.length) return null

  const combine = schedule?.deploy?.combine || 'none'
  if (combine === 'earliest_of') return Math.min(...days)
  if (combine === 'latest_of') return Math.max(...days)
  return days[0]
}
